import { useEffect, useSyncExternalStore } from 'react';
import * as turns from './chatTurns.ts';
import type { TurnOptions } from './chatTurns.ts';
import type { ChatScope } from './scope.ts';

/**
 * Scope-aware chat hook. Pass a scope object from lib/scope.js and it reads
 * that scope's slice from the module-level turn store (lib/chatTurns.js):
 * the persisted thread, the live streaming buffer, status and usage.
 *
 * The turns themselves live in the store, NOT in this hook's state — so a
 * turn started on slide A keeps streaming (into A's slice) while the user
 * switches to slide B and starts another, and switching back to A re-attaches
 * to its still-running stream. send/stop/clearThread/reloadThread delegate to
 * the store keyed by this scope.
 *
 * Returns { messages, streaming, status, usage, activity, send, stop,
 * clearThread, reloadThread } — the same shape the old single-turn hook
 * returned, so CommandBar, Canvas and the library overlay are unchanged.
 * `streaming` is null when this scope is idle, else { text, tools, reasoning }.
 */
export function useChatStream(scope: ChatScope | null | undefined) {
  // Re-render this consumer whenever the store mutates; read the scope's slice
  // fresh each render.
  useSyncExternalStore(turns.subscribe, turns.getVersion, turns.getVersion);
  const slice = turns.getSlice(scope?.key);

  // Opening a scope loads its thread once (keeping any live buffer intact) and
  // clears a waiting 'unread' dot. The cleanup drops it from the viewed set so
  // a turn completing after we leave flags unread instead.
  useEffect(() => {
    if (!scope) return undefined;
    turns.openScope(scope);
    return () => turns.closeScope(scope.key);
  }, [scope?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    messages: slice.messages,
    streaming: slice.streaming,
    status: slice.status,
    usage: slice.usage,
    // This deck's turns only — the store is module-level and keeps other decks'
    // slices for the life of the tab.
    activity: turns.activitySnapshot(scope?.slug),
    send: (prompt: string, opts?: TurnOptions) => turns.runTurn(scope, prompt, opts),
    stop: () => turns.stopTurn(scope?.key),
    clearThread: () => turns.clearTurn(scope),
    reloadThread: () => turns.reloadThread(scope),
  };
}
